// src/utils/uploadToR2Direct.ts
import api from '../api/axios';
import { mixPreviewWeb } from './mixPreviewWeb';
import { getAccessToken } from '../api/axios';

const API_BASE = process.env.REACT_APP_API_BASE_URL as string;

if (!API_BASE) {
  throw new Error("REACT_APP_API_BASE_URL is not defined");
}


type PresignPutResp = {
  /** Presigned PUT url */
  url: string;
  /** Object key R2 will use */
  key: string;
  /** Public read URL (from your Worker / CDN) */
  fileUrl: string;
};
export type BakeOptions = {
  /** e.g. 'warm' | 'cool' | 'noir' | 'none' */
  filterId?: string;
  /** optional BGM track to mix */
  trackId?: string;
  /** bgm volume (0..1) */
  volume?: number;
  /** bgm offset seconds */
  offsetSec?: number;
  /** keep camera/original audio (default true) */
  mixOriginal?: boolean;
  /** override API base (defaults to REACT_APP_API_BASE_URL) */
  apiBase?: string;
  /** auth token (optional override; defaults to in-memory access token) */
  token?: string | null;
  /** customize the final object name */
  rename?: (originalName: string) => string;
  /** override preview timeout (ms) */
  timeoutMs?: number;
};


/**
 * Upload a File to R2 using a presigned PUT URL.
 * If a video filter or music track is provided, it will be baked server-side via /api/videopreview BEFORE upload.
 *
 * @param file          Blob/File to upload
 * @param overrideName  Optional final object key (e.g., "video_20251031T153011Z.mp4")
 * @param bake          Optional baking options (filterId, trackId, etc.)
 * @returns             { key, url } where url is the public read URL
 */
export async function uploadToR2Direct(
  file: File,
  overrideName?: string,
  bake?: BakeOptions
): Promise<{ key: string; url: string }> {
  let uploadFile = file;

  // Bake if either a filter (not 'none') OR a music track is requested.
  const wantsBake =
    !!bake &&
    (Boolean(bake.filterId && bake.filterId !== 'none') || Boolean(bake.trackId)) &&
    (uploadFile.type?.startsWith('video/') || /\.((mp4|mov|m4v|webm))$/i.test(uploadFile.name));

  if (wantsBake) {
    try {
      const token = bake.token ?? getAccessToken() ?? null;
      const apiBase = bake.apiBase ?? API_BASE;

      // Prepare options; include track params. Use `as any` to remain compatible
      // with older mixPreviewWeb typing that might not yet have `trackId`.
      const mpwOpts: any = {
        file: uploadFile,
        filterId: bake.filterId ?? 'none',
        trackId: bake.trackId,             // forwarded to backend if supported
        volume: bake.volume,               // optional
        offsetSec: bake.offsetSec,         // optional
        mixOriginal: bake.mixOriginal ?? true,
        token,
        apiBase,
        timeoutMs: bake.timeoutMs ?? 120_000, // a bit more headroom
      };

      const previewPath = await mixPreviewWeb(mpwOpts);

      const absolutePreviewUrl = toApiAbsolute(previewPath, apiBase);

      // Small retry loop in case the file is not immediately readable
      const bakedBlob = await (async () => {
        const maxAttempts = 4;
        let lastErr: any;
        for (let i = 1; i <= maxAttempts; i++) {
          const resp = await fetch(`${absolutePreviewUrl}?t=${Date.now()}`, {
            headers: { Accept: 'video/*' },
          });
          if (resp.ok) return await resp.blob();
          lastErr = new Error(`Download baked preview failed: ${resp.status}`);
          await new Promise((r) => setTimeout(r, 250 * i)); // 250ms, 500ms, 750ms...
        }
        throw lastErr;
      })();

      // Naming: overrideName wins; else bake.rename; else add -baked
      const baseName = (overrideName || uploadFile.name || 'video.mp4').replace(/^\//, '');
      const targetName =
        overrideName ??
        (bake.rename?.(baseName) ?? withMp4Extension(suffixName(baseName, '-baked')));

      uploadFile = new File([bakedBlob], targetName, { type: 'video/mp4' });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[uploadToR2Direct] Bake failed, uploading original', e);
    }
  }

  // 1) Ask backend to presign a PUT for the EXACT key we want

  const desiredKey = (overrideName || uploadFile.name || 'upload.bin').replace(/^\//, '');
  const contentType = uploadFile.type || 'application/octet-stream';

  // eslint-disable-next-line no-console
  console.log('[uploadToR2Direct] requesting key:', desiredKey, 'type:', contentType);

  const presign = await api.post<PresignPutResp>(
    '/r2/presign/put',
    { key: desiredKey, contentType }
  );
  

  const { url: putUrl, key: serverKey, fileUrl } = presign.data;
  // eslint-disable-next-line no-console
  console.log('[uploadToR2Direct] presign reply:', {
    serverKey,
    fileUrl,
    putUrlPreview: putUrl ? putUrl.slice(0, 60) + '…' : '(missing)',
  });

  if (!putUrl || !serverKey || !fileUrl) {
    throw new Error('Invalid presign response: missing url/key/fileUrl');
  }

  // 2) Upload file via PUT to the presigned URL
  const putResp = await fetch(putUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: uploadFile,
  });

  if (!putResp.ok) {
    const text = await safeText(putResp as any);
    throw new Error(
      `R2 PUT failed: ${putResp.status} ${putResp.statusText}${text ? ` - ${text}` : ''}`
    );
  }

  // 3) Return the public URL (served by your Worker/CDN) and the final key
  // eslint-disable-next-line no-console
  console.log('[uploadToR2Direct] upload complete:', { key: serverKey, url: fileUrl });
  return { key: serverKey, url: fileUrl };
}

/* ---------------- helpers ---------------- */

function toApiAbsolute(p: string, apiBase: string): string {
  if (!p) return '';
  if (/^https?:\/\//i.test(p)) return p;
  const origin = (apiBase || '').replace(/\/$/, '').replace(/\/api\/?$/, '');
  if (p.startsWith('/api/')) return `${origin}${p}`;
  if (p.startsWith('/')) return `${origin}${p}`;
  return `${origin}/${p.replace(/^\/+/, '')}`;
}

function withMp4Extension(name: string): string {
  if (!name) return 'video.mp4';
  return name.toLowerCase().endsWith('.mp4') ? name : name.replace(/\.[^.]+$/, '') + '.mp4';
}

function suffixName(name: string, suffix: string): string {
  const dot = name.lastIndexOf('.');
  if (dot <= 0) return name + suffix;
  return name.slice(0, dot) + suffix + name.slice(dot);
}

async function safeText(res: any) {
  try {
    return await res.text();
  } catch {
    return '';
  }
}
